/**
 * Franjas horarias en que se puede mandar marketing (hora del negocio).
 *
 * Reglas (las mismas que `validarFranjasLocal` y el backend): de 1 a 7 franjas, `desde ≠ hasta`,
 * y `desde > hasta` cruza la medianoche (válido: «de 22 a 2»). Una lista vacía en el oficial es
 * CERRADO, no «sin restricción»: por eso no se puede sacar la última franja.
 *
 * Días: `dias` ausente = todos los días. Destildar TODOS los días de una franja dejaría `[]`,
 * que el backend lee como «todos»: el operador creería que la apagó y seguiría abierta toda la
 * semana. Por eso el último día tildado no se puede destildar; para apagar una franja se la saca.
 */

import { Moon, Plus, Trash2 } from "lucide-react";
import type { FranjaHoraria } from "../types";

interface Props {
  franjas: FranjaHoraria[];
  onChange: (franjas: FranjaHoraria[]) => void;
  /** errores de `validarFranjasLocal` (o del backend) */
  errores: string[];
  disabled?: boolean;
}

export const MAX_FRANJAS = 7;
const TODOS = [0, 1, 2, 3, 4, 5, 6];
/** Orden de la semana argentina (lunes primero); el número es el de `Date.getDay()` (0 = domingo). */
const DIAS: { n: number; corto: string; largo: string }[] = [
  { n: 1, corto: "L", largo: "lunes" },
  { n: 2, corto: "M", largo: "martes" },
  { n: 3, corto: "M", largo: "miércoles" },
  { n: 4, corto: "J", largo: "jueves" },
  { n: 5, corto: "V", largo: "viernes" },
  { n: 6, corto: "S", largo: "sábado" },
  { n: 0, corto: "D", largo: "domingo" },
];

/** true si la franja cruza la medianoche (`22:00 → 02:00`). Con horas inválidas, false. */
export function cruzaMedianoche(f: Pick<FranjaHoraria, "desde" | "hasta">): boolean {
  const re = /^([01]\d|2[0-3]):[0-5]\d$/;
  return re.test(f.desde || "") && re.test(f.hasta || "") && f.desde > f.hasta;
}

export function EditorFranjas({ franjas, onChange, errores, disabled = false }: Props) {
  const lista = franjas || [];

  const cambiar = (i: number, parcial: Partial<FranjaHoraria>) => onChange(lista.map((f, j) => (j === i ? { ...f, ...parcial } : f)));
  const sacar = (i: number) => onChange(lista.filter((_, j) => j !== i));
  const agregar = () => onChange([...lista, { desde: "09:00", hasta: "21:00", dias: [1, 2, 3, 4, 5] }]);

  const alternarDia = (i: number, n: number) => {
    const actuales = lista[i].dias === undefined ? TODOS : lista[i].dias!;
    const tiene = actuales.includes(n);
    if (tiene && actuales.length === 1) return; // ver el comentario de arriba: [] sería «todos»
    const nuevos = tiene ? actuales.filter((d) => d !== n) : [...actuales, n];
    cambiar(i, { dias: [...nuevos].sort((a, b) => a - b) });
  };

  return (
    <div className="space-y-3">
      <ul className="space-y-2">
        {lista.map((f, i) => {
          const dias = f.dias === undefined ? TODOS : f.dias;
          return (
            <li key={i} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-2">
              {/* envuelve: en el celular el segundo horario se salía del recuadro de la franja */}
              <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
                <span>De</span>
                <input type="time" aria-label={`Franja ${i + 1}: desde`} value={f.desde} disabled={disabled}
                  onChange={(e) => cambiar(i, { desde: e.target.value })}
                  className="rounded-lg border border-slate-200 px-2 py-1 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:bg-slate-50" />
                <span>a</span>
                <input type="time" aria-label={`Franja ${i + 1}: hasta`} value={f.hasta} disabled={disabled}
                  onChange={(e) => cambiar(i, { hasta: e.target.value })}
                  className="rounded-lg border border-slate-200 px-2 py-1 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:bg-slate-50" />
                {cruzaMedianoche(f) && (
                  <span className="inline-flex items-center gap-1 text-[11px] text-indigo-600"><Moon size={12} />(cruza la medianoche)</span>
                )}
              </div>

              <div className="flex items-center gap-1" role="group" aria-label={`Franja ${i + 1}: días`}>
                {DIAS.map((d) => {
                  const activo = dias.includes(d.n);
                  return (
                    <button key={d.n} type="button" disabled={disabled} onClick={() => alternarDia(i, d.n)}
                      aria-pressed={activo} aria-label={d.largo} title={d.largo}
                      className={`w-7 h-7 rounded-lg text-[11px] font-bold transition-colors disabled:opacity-50 ${
                        activo ? "bg-violet-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                      }`}>
                      {d.corto}
                    </button>
                  );
                })}
              </div>

              <button type="button" onClick={() => sacar(i)} disabled={disabled || lista.length <= 1}
                aria-label={`Sacar la franja ${i + 1}`}
                title={lista.length <= 1 ? "Tiene que haber al menos una franja" : "Sacar la franja"}
                className="ml-auto w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400">
                <Trash2 size={14} />
              </button>
            </li>
          );
        })}
      </ul>

      <button type="button" onClick={agregar} disabled={disabled || lista.length >= MAX_FRANJAS}
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40">
        <Plus size={13} /> Agregar franja
      </button>

      {errores.length > 0 && (
        <ul className="space-y-1">
          {errores.map((e, i) => <li key={i} className="text-xs text-red-600">{e}</li>)}
        </ul>
      )}

      <p className="text-[11px] text-slate-400">
        Por defecto: lunes a viernes de 9 a 21 y sábados de 9 a 13 (Decreto 2501/2014). Cambiala sólo con el aval legal.
      </p>
    </div>
  );
}

export default EditorFranjas;
